import React from "react";
import PropTypes from "prop-types";
import Button from "../Button";
import QuotableSorter from "./QuotableSorter";
import LaboratorySuiteSearcher from "./LaboratorySuiteSearcher";
import Quotable from "./Quotable";
import NewQuoteGroup from "./NewQuoteGroup";
import TestFinder from "./TestFinder";
import { FullRow } from "./FullRow";
import { post } from "../../lib/Fetching";
import { confirmDialog } from "../../lib/Dialog";
import { deleteQuoteItems } from "../../lib/QuoteActions";
import PriceInput from "./PriceInput";
import GroupPriceSettings from "./GroupPriceSettings";
import GroupRenamer from "./GroupRenamer";
import _ from "underscore";

class QuotableGroup extends React.Component {
  state = {
    reordering: false,
    addingLaboratorySuite: false,
    addingNewGroup: false,
    isRenamingGroup: false,
    isAddingTests: false,
    isChangingPriceSettings: false,
    collapsed: this.props.initialCollapse == false ? false : true,
    // Show the delete checkbox?
    allowSelect: this.props.allowSelect,
    // Which direct descendent quotables are selected for deletion
    selectedQuotables: [],
  };

  constructor(props) {
    super(props);
    this.quotableRefs = {};
  }

  // Only one form can be visible
  toggleForm(name = null) {
    var toggleForms = {
      toggleReordering: false,
      addingLaboratorySuite: false,
      addingNewGroup: false,
      isAddingTests: false,
      isChangingPriceSettings: false,
      isRenamingGroup: false,
    };
    if (name) {
      toggleForms = {
        ...toggleForms,
        collapsed: !this.state[name] ? false : this.state.collapsed,
        ...{ [name]: !this.state[name] },
      };
    }
    this.setState(toggleForms);
  }

  prices = () => {
    const { quotable } = this.props;
    const { quote_item_id, can_add_price } = quotable;
    var prices;
    if (can_add_price) {
      prices = [
        {
          quote_item_id,
          price: this.priceInput.price(),
          changed: this.priceInput.hasChanged(),
        },
      ];
    } else {
      prices = _.compact(
        _.flatten(
          quotable.quotables.map((q) => {
            const component = this.quotableRefs[q.quote_item_id];
            if (!component) {
              // if component is not visible, then do nothing
              return null;
            } else {
              return component.prices();
            }
          })
        )
      );
    }
    return prices;
  };

  toggleAddingLaboratorySuites = () => this.toggleForm("addingLaboratorySuite");
  toggleAddingNewGroup = () => this.toggleForm("addingNewGroup");
  toggleAddingTests = () => this.toggleForm("isAddingTests");
  toggleChangingPriceSettings = () => this.toggleForm("isChangingPriceSettings");
  toggleRenamingGroup = () => this.toggleForm("isRenamingGroup");
  toggleReordering = () => this.toggleForm("reordering");

  toggleFormsAndRefresh = () => {
    this.toggleForm();
    this.reloadData();
  };

  onSuitesAdded = () => {
    this.toggleAddingLaboratorySuites();
    this.setState({ collapsed: false });
    this.reloadData();
  };

  onGroupAdded = () => {
    this.toggleAddingNewGroup();
    this.setState({ collapsed: false });
    this.reloadData();
  };

  onUpdatedPricingSettings = () => {
    this.toggleChangingPriceSettings();
    this.reloadData();
  };

  expandCollapse = () => {
    const { reordering, collapsed } = this.state;
    if (!reordering) this.setState({ collapsed: !collapsed });
  };

  savePositions = () => {
    this.sorter.save(() => {
      this.reloadData();
      this.toggleReordering();
    });
  };

  handleAddTestsSubmit = (test_ids) => {
    const { quotable, attempt_id } = this.props;
    post("/quote_groups/add_tests.json", {
      test_ids,
      attempt_id,
      quote_group_id: quotable.quote_group_id,
    })
      .then((_) => {
        this.toggleAddingTests();
        this.reloadData();
      })
      .catch((e) => alert("Could not add tests: " + e.message));
  };

  handleSelect = (event) => {
    const { toggleDeleteSelect, quotable } = this.props;
    const selected = event.target.checked;
    this.unselectAll();
    toggleDeleteSelect(quotable, selected);
  };

  // Called by children quotables when the delete checkbox is clicked / selected
  toggleDeleteSelect = (quotable, selected) => {
    const { selectedQuotables } = this.state;
    this.setState({
      selectedQuotables: selected
        ? selectedQuotables.concat(quotable)
        : selectedQuotables.filter((q) => q !== quotable),
    });
  };

  unselectAll = (_) => {
    this.setState({ selectedQuotables: [] });
  };

  deleteSelected = (_) => {
    const { quotable, attempt_id } = this.props;
    const { selectedQuotables } = this.state;

    confirmDialog(
      "Are you sure",
      `You are about to delete ${selectedQuotables.length} items.`,
      () =>
        deleteQuoteItems(
          attempt_id,
          quotable.quote_group_id,
          selectedQuotables.map((q) => q.quote_item_id)
        )
          .then((json) => {
            this.reloadData();
            this.setState({
              selectedQuotables: [],
            });
          })
          .catch((e) => alert("Could not delete quote items: " + e.message))
    );
  };

  toggleCollapseOnQuote = (_) => {
    const { quotable } = this.props;
    post("/quote_groups/collapse_toggle.json", { quote_group_id: quotable.quote_group_id })
      .then((_) => this.reloadData())
      .catch((e) => alert("Could not update: " + e.message));
  };

  reloadData = (_) => {
    this.props.rootGroupActions.refreshGroupData();
  };

  renderDeleteButtonRow = () => {
    const { selectedForDeletion } = this.props;
    const { selectedQuotables } = this.state;
    if (selectedForDeletion || selectedQuotables.length == 0) return null;
    return (
      <FullRow showFirstColumn>
        <div className="pull-right" style={{ marginRight: 180 }}>
          <Button title="Un-select All" tiny onClick={this.unselectAll} />
          &nbsp; &nbsp;
          <Button
            title="Delete Selected"
            onClick={this.deleteSelected}
            tiny
            deleting
            icon="fa-trash"
          />
        </div>
      </FullRow>
    );
  };

  renderReorderingButtons() {
    return (
      <div className="pull-right">
        <Button
          title="Save Positions"
          onClick={this.savePositions}
          primary
          tiny
          icon={"fa-save"}
        />{" "}
        &nbsp;
        <Button
          title="Sort Alphabetically"
          onClick={() => this.sorter.sortAlphabetically()}
          tiny
        />{" "}
        &nbsp;
        <Button title="Cancel" onClick={this.toggleReordering} tiny />
      </div>
    );
  }

  renderStandardButtons() {
    const { quotable, hideCollapsed } = this.props;
    const {
      addingLaboratorySuite,
      addingNewGroup,
      isAddingTests,
      isRenamingGroup,
    } = this.state;
    return (
      <div className="group-actions pull-right">
        {!hideCollapsed && (
          <i
            style={{ cursor: "pointer" }}
            onClick={this.toggleCollapseOnQuote}
            className={quotable.collapsed ? "fa fa-compress" : "fa fa-expand"}
          />
        )}
        {quotable.locked && <i className="fa fa-lock" style={{ paddingLeft: 10 }} />}
        {!quotable.locked && (
          <React.Fragment>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <Button
              title="Re-order"
              onClick={this.toggleReordering}
              tiny
              icon={"fa-sort"}
              hideWhenDisabled
              disabled={quotable.quotables.length <= 1}
            />
            &nbsp;
            <Button
              title="Laboratory Suites"
              highlight={addingLaboratorySuite}
              tiny
              icon={"fa-plus"}
              onClick={this.toggleAddingLaboratorySuites}
            />
            &nbsp;
            <Button
              title="Empty Group"
              highlight={addingNewGroup}
              tiny
              icon={"fa-plus"}
              onClick={this.toggleAddingNewGroup}
            />
            &nbsp;
            <Button
              title="Tests"
              tiny
              highlight={isAddingTests}
              icon={"fa-plus"}
              onClick={this.toggleAddingTests}
            />
            &nbsp;
            <Button
              title="Rename"
              icon={"fa-edit"}
              tiny
              highlight={isRenamingGroup}
              onClick={this.toggleRenamingGroup}
            />
            &nbsp;
          </React.Fragment>
        )}
      </div>
    );
  }

  renderQuotables() {
    const {
      quotable,
      allowSelect: allowSelectFromParent,
      attempt_id,
      rootGroupActions,
      selectedForDeletion,
      hideCollapsed,
    } = this.props;
    const { selectedQuotables } = this.state;
    return quotable.quotables
      .sort((a, b) => a.position - b.position)
      .map((q) => (
        <Quotable
          ref={(ref) => (this.quotableRefs[q.quote_item_id] = ref)}
          hideCollapsed={hideCollapsed || quotable.collapsed}
          selectedForDeletion={selectedQuotables.includes(q)}
          toggleDeleteSelect={this.toggleDeleteSelect}
          attempt_id={attempt_id}
          key={q.quote_item_id}
          allowSelect={!quotable.locked && allowSelectFromParent && !selectedForDeletion}
          quotable={q}
          rootGroupActions={rootGroupActions}
        />
      ));
  }

  renderSorter() {
    const { reordering } = this.state;
    const { quotable, attempt_id } = this.props;
    if (!reordering) return null;
    return (
      <FullRow showFirstColumn>
        <QuotableSorter
          attempt_id={attempt_id}
          quotables={quotable.quotables}
          padding={this.indent() + 10}
          ref={(instance) => {
            this.sorter = instance;
          }}
        />
      </FullRow>
    );
  }

  renderTestFinder() {
    const { quotable } = this.props;
    const { isAddingTests } = this.state;
    const existingTestIds = quotable.quotables.filter((q) => q.test_id).map((q) => q.test_id);
    return (
      isAddingTests && (
        <FullRow>
          <TestFinder
            matrix_id={quotable.matrix_id}
            onCancel={this.toggleAddingTests}
            onSubmit={this.handleAddTestsSubmit}
          />
        </FullRow>
      )
    );
  }

  renderLaboratorySuiteAdder() {
    const { addingLaboratorySuite } = this.state;
    const { attempt_id, quotable } = this.props;
    return (
      addingLaboratorySuite && (
        <FullRow>
          <LaboratorySuiteSearcher
            attempt_id={attempt_id}
            matrixLocked
            matrix_id={quotable.matrix_id}
            parent_quote_group_id={quotable.quote_group_id}
            onSuitesAdded={this.onSuitesAdded}
            onCancel={this.toggleAddingLaboratorySuites}
          />
        </FullRow>
      )
    );
  }

  renderNewGroupAdder() {
    const { addingNewGroup } = this.state;
    const {
      attempt_id,
      quotable: { matrix_id, quote_group_id },
    } = this.props;
    if (!addingNewGroup) return null;
    return (
      <FullRow>
        <NewQuoteGroup
          fixed_matrix_id={matrix_id}
          parent_quote_group_id={quote_group_id}
          attempt_id={attempt_id}
          onGroupAdded={this.onGroupAdded}
          onCancel={this.toggleAddingNewGroup}
        />
      </FullRow>
    );
  }

  indent = () => {
    const { quotable } = this.props;
    return quotable.level * 18 + 10;
  };

  renderPriceSettings = () => {
    const { quotable, attempt_id } = this.props;
    const { isChangingPriceSettings } = this.state;
    return (
      isChangingPriceSettings && (
        <FullRow noHover>
          <GroupPriceSettings
            attempt_id={attempt_id}
            quotable={quotable}
            onCancel={this.toggleChangingPriceSettings}
            onUpdated={this.onUpdatedPricingSettings}
          />
        </FullRow>
      )
    );
  };

  renderRenamingGroup = () => {
    const { quotable, attempt_id } = this.props;
    const { isRenamingGroup } = this.state;
    if (!isRenamingGroup) return null;
    return (
      <FullRow>
        <GroupRenamer
          attempt_id={attempt_id}
          quotable={quotable}
          onCancel={this.toggleRenamingGroup}
          onUpdated={this.toggleFormsAndRefresh}
        />
      </FullRow>
    );
  };

  render() {
    const {
      quotable,
      allowSelect,
      selectedForDeletion,
      rootGroupActions: { priceHasChanged },
    } = this.props;
    const { reordering, collapsed } = this.state;
    return (
      <React.Fragment key={quotable.quote_group_id}>
        <tr>
          <td>0</td>
          <td style={{ paddingLeft: this.indent() }} colSpan={4}>
            {collapsed ? <i className="fa fa-caret-down" /> : <i className="fa fa-caret-up" />}
            &nbsp;&nbsp;
            <span className="quote_group" onClick={this.expandCollapse}>
              {quotable.name}
            </span>
            {reordering ? this.renderReorderingButtons() : this.renderStandardButtons()}
          </td>
          <td>
            {allowSelect && (
              <input
                tabIndex={-1}
                type="checkbox"
                checked={selectedForDeletion}
                onChange={this.handleSelect}
              />
            )}
          </td>
          <td>
            {
              <PriceInput
                key={[quotable.id, quotable.price]}
                ref={(ref) => (this.priceInput = ref)}
                hideInput={!quotable.can_add_price}
                showButton={quotable.can_change_pricing_method}
                onClick={this.toggleChangingPriceSettings}
                value={quotable.price}
                onPriceChange={priceHasChanged}
                displayValue={
                  !quotable.can_add_price &&
                  quotable.sum_prices &&
                  (quotable.within_quotable_group || quotable.quote_as_group)
                }
              />
            }
          </td>
        </tr>
        {this.renderRenamingGroup()}
        {this.renderPriceSettings()}
        {this.renderDeleteButtonRow()}
        {this.renderTestFinder()}
        {this.renderLaboratorySuiteAdder()}
        {this.renderNewGroupAdder()}
        {this.renderSorter()}
        {!reordering && !collapsed && this.renderQuotables()}
        {!collapsed &&
          quotable.quotables.length == 0 && (
            <tr>
              <td />
              <td colSpan={6} style={{ paddingLeft: this.indent() + 30 }}>
                [This suite has no items]
              </td>
            </tr>
          )}
      </React.Fragment>
    );
  }
}

export default QuotableGroup;
